import React, {useState} from 'react';
import {IconPlus} from '@tabler/icons-react';
import {StatisticFilterDef} from '../types';
import {SelectModal} from './SelectModal';
import {CURRENT_USER_LABEL, CURRENT_USER_TOKEN, currentUserId} from '../utils/statisticValues';

interface StatisticRecordPickerProps {
    filter: Pick<StatisticFilterDef, 'code' | 'type' | 'targetScreenId'>;
    value: string;
    onChange: (value: string) => void;
    /** Builder defaults keep "Aktuálny používateľ" as the token, so every viewer starts on themselves. */
    currentUserAsToken?: boolean;
    /** Shown but not changeable (a filter viewers may not change). */
    disabled?: boolean;
}

/**
 * Value of a user / many-to-one statistic filter in the relation-picker style:
 * a badge with the picked record, a click opens the filter's screen in a modal.
 */
export const StatisticRecordPicker: React.FC<StatisticRecordPickerProps> = ({filter, value, onChange, currentUserAsToken = false, disabled = false}) => {
    const [open, setOpen] = useState(false);
    const [picked, setPicked] = useState<{value: string; label: string} | null>(null);

    if (!filter.targetScreenId) {
        return (
            <input
                type="number"
                className="form-control form-control-sm"
                placeholder="ID záznamu"
                value={value}
                disabled={disabled}
                onChange={(e) => onChange(e.target.value)}
            />
        );
    }

    const isCurrentUser = filter.type === 'user' && value !== '' && (value === CURRENT_USER_TOKEN || value === currentUserId());
    const label = picked && picked.value === value
        ? picked.label
        : isCurrentUser ? CURRENT_USER_LABEL : value !== '' ? `ID ${value}` : null;

    const pick = (next: string, nextLabel: string) => {
        setPicked({value: next, label: nextLabel});
        onChange(next);
    };

    return (
        <>
            <div
                className="form-select form-select-sm d-flex align-items-center gap-1"
                style={{
                    cursor: disabled ? 'default' : 'pointer',
                    backgroundColor: disabled ? 'var(--bs-secondary-bg)' : undefined,
                    backgroundImage: 'none',
                    paddingRight: '0.5rem',
                    height: '32px',
                    minHeight: '32px',
                    overflow: 'hidden',
                }}
                role={disabled ? undefined : 'button'}
                tabIndex={disabled ? -1 : 0}
                aria-disabled={disabled || undefined}
                data-e2e={`statistic-picker:${filter.code}`}
                onClick={(e) => {
                    if (disabled || (e.target as HTMLElement).closest('.relation-pick-remove')) return;
                    setOpen(true);
                }}
            >
                {!label && (
                    <span className="text-muted small d-inline-flex align-items-center gap-1">
                        <IconPlus size={14}/> Vybrať...
                    </span>
                )}
                {label && (
                    <span
                        className="badge bg-secondary-subtle text-secondary-emphasis border d-inline-flex align-items-center gap-1 flex-shrink-0"
                        style={{fontWeight: 500, padding: '0.15em 0.45em', lineHeight: 1.3}}
                    >
                        {label}
                        {!disabled && <span
                            className="relation-pick-remove"
                            role="button"
                            aria-label="Odstrániť"
                            onClick={(e) => { e.stopPropagation(); onChange(''); }}
                            style={{cursor: 'pointer', lineHeight: 1, fontSize: '0.95rem'}}
                        >
                            ×
                        </span>}
                    </span>
                )}
            </div>

            {open && (
                <SelectModal
                    show
                    onHide={() => setOpen(false)}
                    screenId={filter.targetScreenId}
                    pickOnly
                    onPickCurrentUser={filter.type === 'user' && currentUserId() ? () => {
                        pick(currentUserAsToken ? CURRENT_USER_TOKEN : currentUserId() as string, CURRENT_USER_LABEL);
                        setOpen(false);
                    } : undefined}
                    onSelect={(row: any, displayValue: string) => {
                        pick(String(row.id), displayValue);
                        setOpen(false);
                    }}
                />
            )}
        </>
    );
};
